// NotificationPreferencesPage — "which of these emails do I want?", one
// row per event, one checkbox each.
//
// Distinct from NotificationSettingsPage, which is the admin console: a
// GLOBAL kill switch per type, for the whole platform. This page is
// personal, every signed-in user reaches it, and unticking a box here
// stops that one event reaching that one inbox.
//
// A member holding the "manage notifications" permission (and company
// admins) can also open the list of anyone on a project they run -
// colleagues and partners - and change it: the people running the job
// decide what their partners are told. That is why every row says who
// last set it: mail quietly stopping is bad enough without it being
// anonymous. Everyone else sees only their own list, and a partner only
// the few mails partners receive (the server decides both).
//
// Data:
//   GET  /notification-settings/me
//   GET  /notification-settings/user/:userId
//   PUT  /notification-settings/user/:userId   { source, enabled }
//
// Enforcement is nowhere near here - it is in back/utils/mailAudit, the
// one point every outgoing email passes through, so a box unticked here
// holds for the app, the worker and the queue at once.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Card, Checkbox, Empty, Select, Skeleton, Table, Tag, Tooltip, message } from "antd";
import { useSelector } from "react-redux";
import moment from "moment";
import { url, tokenRef } from "api";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import useLastProject from "components/Coordination/useLastProject";
import { T } from "pages/Issue/theme";
import { rowFromWriteResponse } from "./writeResponse";
import { PartnerWithTrades } from "components/Partners/PartnerTradeLabel";
import { cadenceCell, showCadenceColumn, CADENCE_FOOTNOTE } from "./cadenceView";
import { matchOption } from "components/SearchSelect/searchSelect";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });

// Same three buckets the admin console uses, and for the same reason: a
// digest and a share link are not the same kind of decision.
const GROUPS = [
  { key: "scheduled", title: "Scheduled", hint: "Sent on a clock — digests and reports." },
  { key: "event", title: "When something happens", hint: "Sent the moment something changes on a project you are on." },
  { key: "manual", title: "When someone sends it", hint: "Sent only when a person explicitly triggers them." },
];
function groupOf(when) {
  const w = String(when || "").toLowerCase();
  if (/daily|monday|nightly|month/.test(w)) return "scheduled";
  if (/manual/.test(w)) return "manual";
  return "event";
}

async function getJson(path) {
  const r = await fetch(`${url}${path}`, { headers: auth() });
  const b = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`);
  return b;
}

export default function NotificationPreferencesPage() {
  const me = useSelector((s) => s.user?.userInfo) || {};
  const myId = String(me._id || me.id || "");

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useLastProject(projects);
  const [roster, setRoster] = useState([]);
  // Whose list is on screen. Empty = mine.
  const [targetId, setTargetId] = useState("");
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState("");
  // From GET /me: may this person open other people's lists, and are they
  // a partner (their list is then the partner mails only).
  const [canManageOthers, setCanManageOthers] = useState(false);
  const [isPartner, setIsPartner] = useState(false);

  useEffect(() => {
    fetch(`${url}/projects/picker`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setProjects(Array.isArray(rows) ? rows : rows?.projects || []))
      .catch(() => setProjects([]));
  }, []);

  // The people whose preferences this user may open: everyone on the
  // chosen project's roster. The server is the authority - it answers
  // 403 for anyone the caller does not share a project with - so this
  // list is a convenience, not the gate.
  useEffect(() => {
    if (!projectId || !canManageOthers) { setRoster([]); return; }
    fetch(`${url}/projects/${projectId}/participants/roster`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : []))
      .then((g) => setRoster(Array.isArray(g) ? g : []))
      .catch(() => setRoster([]));
  }, [projectId, canManageOthers]);

  const load = useCallback(async (who) => {
    setLoading(true); setError("");
    try {
      const body = await getJson(who ? `/notification-settings/user/${who}` : "/notification-settings/me");
      setEvents(body.events || []);
      if (!who) {
        setCanManageOthers(!!body.canManageOthers);
        setIsPartner(!!body.partner);
      }
    } catch (e) {
      setError(e.message || "Could not load notification preferences");
      setEvents(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(targetId); }, [targetId, load]);

  const people = useMemo(() => {
    const opts = [{ value: "", label: `My notifications (${me.email || "me"})` }];
    for (const co of roster || []) {
      const inner = (co.users || [])
        .filter((u) => u.userId && String(u.userId) !== myId)
        .map((u) => ({
          value: String(u.userId),
          label: `${u.name || u.email}${u.email ? ` — ${u.email}` : ""}`,
        }));
      // The company with its trades beneath (owner, 2026-10-16).
      if (inner.length) opts.push({ label: <PartnerWithTrades name={co.companyName || "Company"} trades={(co.tradeLabels && co.tradeLabels.length ? co.tradeLabels : co.trades) || []} />, options: inner });
    }
    return opts;
  }, [roster, me.email, myId]);

  const toggle = async (row, next) => {
    const who = targetId || myId;
    setSaving(row.source);
    // Optimistic: the checkbox is the point of the page, and a spinner
    // per row on a 29-row table reads as broken.
    setEvents((prev) => prev.map((e) => (e.source === row.source ? { ...e, enabled: next } : e)));
    try {
      const r = await fetch(`${url}/notification-settings/user/${who}`, {
        method: "PUT",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ source: row.source, enabled: next }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`);
      // THE STORED ROW BEATS WHAT THIS PAGE BELIEVES IT SENT.
      //
      // This used to write `enabled: next` - the value it had optimistically
      // painted - and take only setBy from the answer. So the checkbox showed
      // the page's intention rather than the database's row, which is fine
      // until they differ, and they CAN differ: the route answers
      // `{ raced: true }` with a 200 AND NO ROW when two writers hit the
      // unique index on userId+source together (routes/notificationSettings.js,
      // the 11000 branch). On that path `b.setBy` is undefined, so this BLANKED
      // the "set by" attribution while leaving the checkbox at whatever this
      // page had assumed - a page saying "saved, by nobody" for a write it did
      // not necessarily win.
      //
      // A race is the one case the flag exists for, so it is the one case that
      // must not be guessed. Written to work whichever way the route goes: if
      // the answer carries a row, render THAT; if it carries no row, re-read.
      const applied = rowFromWriteResponse(b);
      if (applied) {
        setEvents((prev) => prev.map((e) => (e.source === row.source
          ? { ...e, ...applied, setByName: applied.setBy ? (me.name || "you") : "" }
          : e)));
      } else {
        // No row came back. Re-read rather than keep the optimistic value: a
        // whole-table reload is a visible cost on a rare race, and it is the
        // honest one - the alternative is a checkbox that reports a write it
        // cannot confirm.
        await load(targetId);
      }
    } catch (e) {
      setEvents((prev) => prev.map((x) => (x.source === row.source ? { ...x, enabled: !next } : x)));
      message.error(e.message || "Could not save that");
    } finally {
      setSaving("");
    }
  };

  // HOW OFTEN, for the three digests that have a weekday gate behind them.
  //
  // Sends ONLY cadence, because absent means unchanged on this route - so
  // changing how often a digest arrives cannot flip whether it arrives at all.
  // Clearing the picker sends null EXPLICITLY, which is how the API says "back
  // to the default" as opposed to "leave it alone".
  const setCadence = async (row, value) => {
    const who = targetId || myId;
    setSaving(row.source);
    try {
      const r = await fetch(`${url}/notification-settings/user/${who}`, {
        method: "PUT",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ source: row.source, cadence: value === undefined ? null : value }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`);
      const applied = rowFromWriteResponse(b);
      // MERGED, never replaced: the write answers with seven fields and none of
      // them is the label, the "when" text or the description. Replacing the row
      // would empty those columns as you used the page.
      if (applied) {
        setEvents((prev) => prev.map((e) => (e.source === row.source
          ? { ...e, ...applied, setByName: applied.setBy ? (me.name || "you") : "" }
          : e)));
      } else {
        await load(targetId);
      }
    } catch (e) {
      message.error(e.message || "Could not save that");
    } finally {
      setSaving("");
    }
  };

  // The column exists only when the DATA says some row has a choice, so this
  // ships before the API answers and stays invisible until it does.
  const cadenceColumn = {
    title: (
      <Tooltip title={CADENCE_FOOTNOTE}>
        <span>How often</span>
      </Tooltip>
    ),
    dataIndex: "cadence", width: 190,
    render: (_v, r) => {
      const cell = cadenceCell(r);
      return (
        <div>
          {cell.picker ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Select
                size="small"
                style={{ width: 108 }}
                value={cell.value}
                options={cell.options}
                onChange={(v) => setCadence(r, v)}
                disabled={saving === r.source}
                // Clearing is the only way to say "use the default again".
                allowClear={!cell.isDefault}
                placeholder="Not set"
              />
              {cell.isDefault ? (
                <Tooltip title="Nobody has set this, so it follows the default.">
                  <Tag style={{ marginInlineEnd: 0, fontSize: 10 }}>default</Tag>
                </Tooltip>
              ) : null}
            </span>
          ) : (
            <span style={{ fontSize: 12, color: T.muted }}>{cell.text}</span>
          )}
          {/* What they chose, and that it stopped counting. Both halves. */}
          {cell.note ? (
            <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>{cell.note}</div>
          ) : null}
        </div>
      );
    },
  };

  const columns = [
    {
      title: "Email", dataIndex: "label", width: "34%",
      render: (label, r) => (
        <div>
          <div style={{ fontWeight: 500, color: T.ink }}>{label}</div>
          <div style={{ fontSize: 11, color: T.muted, fontFamily: "monospace" }}>{r.source}</div>
        </div>
      ),
    },
    { title: "When", dataIndex: "when", width: "16%", render: (w) => <span style={{ fontSize: 12, color: T.muted }}>{w}</span> },
    { title: "What it is", dataIndex: "desc", render: (d) => <span style={{ fontSize: 12, color: T.text }}>{d}</span> },
    {
      title: "Send it", dataIndex: "enabled", width: 130, align: "center",
      render: (on, r) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Checkbox
            checked={!!on}
            disabled={saving === r.source}
            onChange={(e) => toggle(r, e.target.checked)}
          />
          {/* Somebody else decided this. Say so, and say who. */}
          {r.setByName ? (
            <Tooltip title={`Set by ${r.setByName}${r.setAt ? ` on ${moment(r.setAt).format("D MMM YYYY")}` : ""}`}>
              <Tag color="gold" style={{ marginInlineEnd: 0, fontSize: 10 }}>set for you</Tag>
            </Tooltip>
          ) : null}
        </span>
      ),
    },
    // Appended rather than inserted, so the four columns a person already knows
    // keep their positions when this appears.
    ...(showCadenceColumn(events || []) ? [cadenceColumn] : []),
  ];

  const sections = GROUPS
    .map((g) => ({ ...g, rows: (events || []).filter((e) => groupOf(e.when) === g.key) }))
    .filter((g) => g.rows.length);

  const off = (events || []).filter((e) => !e.enabled).length;

  return (
    <div style={{ padding: 24, maxWidth: 1180, fontFamily: "Inter, system-ui, sans-serif" }}>
      <CoordinationBreadcrumbs current="Email preferences" />
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ margin: 0, color: T.ink }}>Email preferences</h2>
        <div style={{ color: T.muted, fontSize: 13, marginTop: 4 }}>
          {isPartner
            ? "The emails BidLight sends you about the work you are part of, and whether you want each. Unticking one stops it for you only."
            : "Every email BidLight can send you, and whether you want it. Unticking one stops that email for this person only — everyone else still gets it."}
        </div>
      </div>

      {canManageOthers ? (
      <Card size="small" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: T.muted }}>Project</span>
          <Select filterOption={matchOption} style={{ width: 250 }} value={projectId || undefined} onChange={setProjectId}
                  placeholder="pick a project to list its people" allowClear showSearch optionFilterProp="label"
                  options={projects.map((p) => ({ value: p._id, label: p.title || "Untitled" }))} />
          <span style={{ fontSize: 12, color: T.muted }}>Whose emails</span>
          <Select filterOption={matchOption} style={{ width: 330 }} value={targetId} onChange={setTargetId}
                  showSearch optionFilterProp="label" options={people} />
          {off > 0 && (
            <Tag color="orange">{off} switched off</Tag>
          )}
        </div>
        {targetId ? (
          <Alert type="info" showIcon style={{ marginTop: 10 }}
                 message="You are changing someone else's email preferences. They will see that you set it." />
        ) : null}
      </Card>
      ) : null}

      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} /> : null}
      {loading ? <Skeleton active /> : null}
      {!loading && events && !sections.length ? <Empty description="No notification types" /> : null}

      {!loading && sections.map((g) => (
        <Card key={g.key} size="small" style={{ marginBottom: 14 }}
              title={<span>{g.title} <span style={{ fontWeight: 400, fontSize: 12, color: T.muted }}>— {g.hint}</span></span>}>
          <Table
            rowKey="source"
            size="small"
            pagination={false}
            columns={columns}
            dataSource={g.rows}
          />
        </Card>
      ))}
    </div>
  );
}
